import useBusinessTravellerSocieteDetailsFetcher from 'businessTraveller/hooks/useBusinessTravellerSocieteDetailsFetcher'
import useSocieteCampaignTargetTier from 'loyalty/hooks/useSocieteCampaignTargetTier'
import { isAtOrAboveLoyaltyTier } from 'loyalty/utils/isAtOrAboveLoyaltyTier'
import useEmployeeOrders from 'businessTraveller/hooks/useEmployeeOrders'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOnboardingStatusResolved from 'businessTraveller/hooks/useOnboardingStatusResolved'

/**
 * Determines the active Société notification and returns the active notification type
 *
 * A notification is only eligible when ALL of the following base conditions are met:
 * - All required data has finished fetching (business account, business settings, linked credit account, employee notification)
 * - The `lebtSociete` feature flag is enabled
 * - The Société tier setting is enabled in business settings
 * - The employee's current loyalty tier is below the target tier
 * - On the landing page: the onboarding modal has been dismissed or completed
 *
 * Individual notification conditions (evaluated in priority order):
 * TODO [societe]: Add completion notification
 *
 * 1. **Almost There** — shown when the employee's completed qualifying bookings equal
 *    `businessSettings.threshold - 1` (one booking away from the target tier)
 *    and the notification has not been dismissed.
 *
 * 2. **Unlock Tier** — shown when the base conditions are met and the notification
 *    has not been dismissed. Also, the higher-priority Almost There notification has not been dismissed
 */
function useSocieteNotificationFetcher(): App.BusinessTraveller.SocieteNotificationType | null {
  const isSocieteEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lebtSociete)
  const { targetTier } = useSocieteCampaignTargetTier()
  // Fetch the Société details including the tier setting, linked credit account, and employee notification
  const { isFetching, societeTierSetting, linkedCreditAccount, employeeNotification } = useBusinessTravellerSocieteDetailsFetcher(targetTier)

  // Fetch the employee's completed qualifying bookings
  const { total: qualifiedBookings, isLoading: isLoadingOrders } = useEmployeeOrders({
    timeframe: 'qualificationWindow',
    page: 0,
    limit: 1,
    filterBy: 'traveller',
  })

  // Onboarding status should be resolved before showing societe notification on landing page
  const isOnboardingStatusResolved = useOnboardingStatusResolved()

  // Check if the employee's current loyalty tier is above or equal to the target tier
  const isAtOrAboveTargetTier = isAtOrAboveLoyaltyTier(linkedCreditAccount?.tier, targetTier)

  // Check if all required data has finished fetching
  const isDataFetched = !isFetching && !isLoadingOrders

  // Check if the Société notification should be shown based on the base conditions
  const shouldShowSocieteNotification = isDataFetched && isSocieteEnabled && !!societeTierSetting?.enabled && !isAtOrAboveTargetTier && isOnboardingStatusResolved

  /**
   * Check if the Almost There notification is eligible
   * - The Société notification should be shown
   * - The employee's completed qualifying bookings equal `businessSettings.threshold - 1` (one booking away from the target tier)
   * - The Almost There notification has not been dismissed yet
   */
  const isAlmostThereEligible =
    shouldShowSocieteNotification
    && (societeTierSetting?.threshold !== undefined && qualifiedBookings === societeTierSetting.threshold - 1)
    && !employeeNotification?.societe?.almostThereDismissed

  /**
   * Check if the Unlock notification is eligible
   * - The Société notification should be shown
   * - The Unlock notification has not been dismissed yet
   * - The higher-priority Almost There notification has not been dismissed
   */
  const isUnlockEligible =
    shouldShowSocieteNotification
    && !employeeNotification?.societe?.unlockTierDismissed
    && !employeeNotification?.societe?.almostThereDismissed

  /**
   * Determine the active notification based on the eligibility
   * - Priority: AlmostThere > Unlock — only one shows at a time
   * - TODO [societe]: Add completion notification
   */
  if (isAlmostThereEligible) return 'ALMOST_THERE'
  if (isUnlockEligible) return 'UNLOCK'
  return null
}

export default useSocieteNotificationFetcher
